// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import TutorialHost from './components/tutorial/TutorialHost';
import { RootState } from './redux/store';
import { useAppDispatch } from './hooks/useAppDispatch';
import { getUserProfile, setUser, setToken } from './redux/auth/authSlice';
import tokenManager from './utils/tokenManager';
import { logger } from './utils/logger';
import { HeaderActionsProvider } from './components/tasky/layout/HeaderActionsContext';
import SubscriptionRoute from './components/routing/SubscriptionRoute';
import PrivateRoute from './routes/PrivateRoute';
import AdminRoute from './routes/AdminRoute';
import { ToastProvider } from './contexts/ToastContext';
import { ConfirmProvider } from './components/tasky/modal/ConfirmProvider';
import { useChannelTalk } from './hooks/useChannelTalk';
import { fetchOrganization, selectOrganization } from './redux/organization/organizationSlice';

// ページのインポート
import LoginPage from './pages/auth/LoginPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import MagicLinkSentPage from './pages/auth/MagicLinkSentPage';
import CompleteSignInPage from './pages/auth/CompleteSignInPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import AdminLoginPage from './pages/auth/AdminLoginPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import ChatPage from './pages/chat/ChatPage';
import ProfilePage from './pages/profile/ProfilePage';
import AccountSettings from './pages/account/AccountSettings';
import AdminPage from './pages/admin/AdminPage';
import BehaviorTrackerRoot from './analytics/BehaviorTrackerRoot';
import OrganizationDetailPage from './pages/admin/organizations/OrganizationDetailPage';
import AdminUserDetailPage from './pages/admin/users/AdminUserDetailPage';
import { OrganizationPage } from './pages/organization/OrganizationPage';
import InviteAcceptPage from './pages/auth/InviteAcceptPage';
import AgentListPage from './pages/agents/AgentListPage';
import AIScorePage from './pages/ai-score/AIScorePage';
import JevEvaluationPage from './pages/jev/JevEvaluationPage';
import DocumentCreationTest from './pages/DocumentCreationTest';
import KnowledgeDashboard from './pages/knowledge/KnowledgeDashboard';
import ImageGenerationPage from './pages/image-generation/ImageGenerationPage';
import CarouselAdsPage from './pages/ads/CarouselAdsPage';
import MovieCreationPage from './pages/ads/MovieCreationPage';
import MovieProjectEditorPage from './pages/ads/MovieProjectEditorPage';
import BannerAdsPage from './pages/ads/BannerAdsPage';
import BannerAbTestListPage from './pages/ads/BannerAbTestListPage';
import BannerAbTestResultPage from './pages/ads/BannerAbTestResultPage';
import VideoAdsPage from './pages/ads/VideoAdsPage';
import MangaAdsPage from './pages/ads/MangaAdsPage';
import LpCreationPage from './pages/ads/LpWorkflowPage';
import MusicCreationPage from './pages/ads/MusicCreationPage';
import SongAdListPage from './pages/ads/SongAdListPage';
import SongAdEditorPage from './pages/ads/SongAdEditorPage';
import UgcAdListPage from './pages/ads/UgcAdListPage';
import UgcAdEditorPage from './pages/ads/UgcAdEditorPage';
import RecipeVideoListPage from './pages/recipe-video/RecipeVideoListPage';
import RecipeVideoEditorPage from './pages/recipe-video/RecipeVideoEditorPage';
import UsageDashboard from './pages/usage/UsageDashboard';
import OnboardingPage from './pages/onboarding/OnboardingPage';
import CompanyOnboardingPage from './pages/onboarding/CompanyOnboardingPage';
import SignupOnboardingPage from './pages/onboarding/SignupOnboardingPage';
import FirstLoginPage from './pages/auth/FirstLoginPage';
import CreditPurchaseResultPage from './pages/credits/CreditPurchaseResultPage';
import NewsListPage from './pages/news/NewsListPage';
import NewsDetailPage from './pages/news/NewsDetailPage';
import HomePage from './pages/home/HomePage';
import RecentWorksPage from './pages/recent-works/RecentWorksPage';
import TaapDashboardRedirect from './components/taap/TaapDashboardRedirect';
import TaapDashboardPage from './pages/taap/TaapDashboardPage';
import TaapProposalsPage from './pages/taap/TaapProposalsPage';
import TaapProposalDetailPage from './pages/taap/TaapProposalDetailPage';
import MediaConnectPage from './pages/settings/MediaConnectPage';
import MetaDashboardPage from './pages/ad-dashboard/MetaDashboardPage';
import GoogleAdsDashboardPage from './pages/ad-dashboard/GoogleAdsDashboardPage';
import LandingPage from './pages/landing/LandingPage';
import SignupPage from './pages/signup/SignupPage';
import DiagnosePage from './pages/diagnose/DiagnosePage';
import AnalyzingPage from './pages/diagnose/AnalyzingPage';
import CompletePage from './pages/diagnose/CompletePage';
import ReportPage from './pages/report/ReportPage';
import MangaPublicPage from './pages/manga/MangaPublicPage';
import PlansPage from './pages/plans/PlansPage';
import { trackMetaPageView } from './utils/metaPixel';
import { V2_BASE } from './v2/paths';
// 新UIで作り直した画面（担当ごとの v2/pages/<担当>/routes.ts）。ここに無い経路は旧画面を枠の中に出す
import { UNIT_ROUTES as V2_UNIT_ROUTES } from './v2/pages/units/registry';
import { RouteGuard as V2RouteGuard } from './v2/pages/units/RouteGuard';
// TestPageは動的インポートするので、ここでは不要

const MetaPixelRouteTracker: React.FC = () => {
  const location = useLocation();
  const isInitialPageViewRef = useRef(true);

  useEffect(() => {
    // public/index.html の Meta Pixel ベースコードが初回 PageView を送信するため、
    // SPA 内の route change のみ追加送信する。
    if (isInitialPageViewRef.current) {
      isInitialPageViewRef.current = false;
      return;
    }

    trackMetaPageView();
  }, [location.pathname, location.search]);

  return null;
};
// 広告着地時の twclid/utm_* をリダイレクト先へ引き継ぐ（クエリ消失で X/GA4 計測が壊れるため）
const NavigateWithQuery = ({ to }: { to: string }) => {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search }} replace />;
};

// 遅延ロード用のコンポーネント
const TestPage = React.lazy(() => import('./pages/test/TestPage'));
const DesignSystemTestPage = React.lazy(() => import('./pages/test/DesignSystemTestPage'));
const ChatV2Page = React.lazy(() => import('./pages/chat-v2/ChatV2Page'));
const VideoEditPage = React.lazy(() => import('./pages/video-edit/VideoEditPage'));
const LpImproveListPage = React.lazy(() => import('./pages/lp-improve/LpImproveListPage'));
const LpImproveWorkspacePage = React.lazy(() => import('./pages/lp-improve/LpImproveWorkspacePage'));
// 新UI（V2_BASE = /test-ui）。見本は _docs/new-ui/concept-board-v2.png
const V2Layout = React.lazy(() => import('./v2/V2Layout'));
// 画面ごとに別のまとまりで読む（作りかけの画面が壊れても、ほかの画面は開ける）
const V2HomePage = React.lazy(() => import('./v2/pages/HomePage'));
const V2BannerPage = React.lazy(() => import('./v2/pages/BannerPage'));
const V2VideoPage = React.lazy(() => import('./v2/pages/VideoPage'));
const V2ProductsPage = React.lazy(() => import('./v2/pages/ProductsPage'));
const V2ProductDetailPage = React.lazy(() => import('./v2/pages/ProductDetailPage'));
const V2LegacyFrame = React.lazy(() => import('./v2/legacy/LegacyFrame'));

const FeatureRoute = ({
  feature,
  children,
}: {
  feature: 'enable_carousel_creation' | 'enable_movie_creation' | 'enable_lp_creation' | 'enable_music_creation' | 'enable_song_ad_creation' | 'enable_ugc_ad_creation' | 'enable_ad_operations' | 'enable_news' | 'enable_ai_doc3' | 'enable_auto_banner_ab_test' | 'enable_chat_tools';
  children: React.ReactNode;
}) => {
  const dispatch = useAppDispatch();
  const organization = useSelector(selectOrganization);
  const { isLoading, error } = useSelector((state: RootState) => state.organization);

  useEffect(() => {
    if (!organization && !isLoading && !error) {
      dispatch(fetchOrganization());
    }
  }, [dispatch, organization, isLoading, error]);

  if (!organization) {
    if (error) {
      return <Navigate to="/home" replace />;
    }
    return <div>Loading...</div>;
  }

  if (!organization[feature]) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};
/**
 * ログイン後の各機能の経路。旧UIの経路として使い、新UI（/test-ui）の枠の中でも同じ表を使う（V2LegacyFrame）。
 */
const appRoutes = (
  <>
    <Route
      path="/home"
      element={
        <PrivateRoute>
          <HomePage />
        </PrivateRoute>
      }
    />
    <Route
      path="/recent-works"
      element={
        <PrivateRoute>
          <RecentWorksPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/dashboard"
      element={
        <PrivateRoute>
          <DashboardPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/chat/:chatId"
      element={
        <PrivateRoute>
          <ChatPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/chat/new"
      element={
        <PrivateRoute>
          <ChatPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/chat/ai"
      element={
        <PrivateRoute>
          <ChatPage />
        </PrivateRoute>
      }
    />
    <Route path="/chat-v2/edit/:editId" element={<PrivateRoute><FeatureRoute feature="enable_chat_tools"><React.Suspense fallback={<div>Loading...</div>}><VideoEditPage /></React.Suspense></FeatureRoute></PrivateRoute>} />
    {/* チャットv2（選択肢カードで進めるチャット）。:chatId? で新規→作成後の遷移でもページを作り直さない */}
    <Route
      path="/chat-v2/:chatId?"
      element={
        <PrivateRoute>
          <FeatureRoute feature="enable_chat_tools">
            <React.Suspense fallback={<div>Loading...</div>}>
              <ChatV2Page />
            </React.Suspense>
          </FeatureRoute>
        </PrivateRoute>
      }
    />
    <Route
      path="/chat/super-agent"
      element={
        <PrivateRoute>
          <ChatPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/profile"
      element={
        <PrivateRoute>
          <ProfilePage />
        </PrivateRoute>
      }
    />

    {/* アカウント設定ルート */}
    <Route
      path="/account/settings"
      element={
        <PrivateRoute>
          <AccountSettings />
        </PrivateRoute>
      }
    />

    {/* AI使用量ダッシュボード */}
    <Route
      path="/usage"
      element={
        <PrivateRoute>
          <UsageDashboard />
        </PrivateRoute>
      }
    />

    {/* AI活用スコアルート */}
    <Route path="/jev" element={<PrivateRoute><JevEvaluationPage /></PrivateRoute>} />
    <Route
      path="/ai-score"
      element={
        <PrivateRoute>
          <AIScorePage />
        </PrivateRoute>
      }
    />

    {/* クレジット追加購入 (Stripe Checkout) の戻り先 */}
    <Route
      path="/credits/purchase/success"
      element={
        <PrivateRoute>
          <CreditPurchaseResultPage type="success" />
        </PrivateRoute>
      }
    />
    <Route
      path="/credits/purchase/cancel"
      element={
        <PrivateRoute>
          <CreditPurchaseResultPage type="cancel" />
        </PrivateRoute>
      }
    />

    {/* 組織関連ルート */}
    <Route
      path="/organization"
      element={
        <PrivateRoute>
          <OrganizationPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/organization/members"
      element={
        <PrivateRoute>
          <OrganizationPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/organization/settings"
      element={
        <PrivateRoute>
          <OrganizationPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/organization/analytics"
      element={
        <PrivateRoute>
          <OrganizationPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/organization/edit"
      element={
        <PrivateRoute>
          <React.Suspense fallback={<div>Loading...</div>}>
            {/* 動的インポートを使用してコンポーネントを遅延ロード */}
            {React.createElement(React.lazy(() => import('./pages/organization/OrganizationEditPage')))}
          </React.Suspense>
        </PrivateRoute>
      }
    />

    {/* エージェント一覧ページ */}
    <Route
      path="/agents"
      element={
        <PrivateRoute>
          <AgentListPage />
        </PrivateRoute>
      }
    />

    {/* AI資料作成ページ */}
    <Route
      path="/document-creation"
      element={
        <PrivateRoute>
          <React.Suspense fallback={<div>Loading...</div>}>
            {React.createElement(React.lazy(() => import('./pages/document/DocumentCreationPage')))}
          </React.Suspense>
        </PrivateRoute>
      }
    />

    {/* 顧客ナレッジ管理ルート */}
    <Route
      path="/knowledge"
      element={
        <KnowledgeDashboard />
      }
    />
    {/* 画像生成ルート */}
    <Route
      path="/image-generation"
      element={
        <PrivateRoute>
          <ImageGenerationPage />
        </PrivateRoute>
      }
    />

    {/* バナー広告ルート */}
    <Route
      path="/ads/banner"
      element={
        <PrivateRoute>
          <BannerAdsPage />
        </PrivateRoute>
      }
    />

    {/* オートバナーABテスト（org フラグ enable_auto_banner_ab_test） */}
    <Route
      path="/ads/banner-ab-tests"
      element={
        <PrivateRoute>
          <FeatureRoute feature="enable_auto_banner_ab_test">
            <BannerAbTestListPage />
          </FeatureRoute>
        </PrivateRoute>
      }
    />
    <Route
      path="/ads/banner-ab-tests/:id"
      element={
        <PrivateRoute>
          <FeatureRoute feature="enable_auto_banner_ab_test">
            <BannerAbTestResultPage />
          </FeatureRoute>
        </PrivateRoute>
      }
    />

    {/* カルーセル広告ルート */}
    <Route
      path="/ads/carousel"
      element={
        <PrivateRoute>
          <FeatureRoute feature="enable_carousel_creation">
            <CarouselAdsPage />
          </FeatureRoute>
        </PrivateRoute>
      }
    />

    {/* ムービー制作ルート（プロジェクト一覧・エディタ） */}
    <Route
      path="/ads/movie"
      element={
        <PrivateRoute>
          <FeatureRoute feature="enable_movie_creation">
            <MovieCreationPage />
          </FeatureRoute>
        </PrivateRoute>
      }
    />
    <Route
      path="/ads/movie/:projectId"
      element={
        <PrivateRoute>
          <FeatureRoute feature="enable_movie_creation">
            <MovieProjectEditorPage />
          </FeatureRoute>
        </PrivateRoute>
      }
    />

    {/* 動画広告ルート */}
    <Route
      path="/ads/video"
      element={
        <PrivateRoute>
          <SubscriptionRoute>
            <VideoAdsPage />
          </SubscriptionRoute>
        </PrivateRoute>
      }
    />

    {/* 漫画広告ルート */}
    <Route
      path="/ads/manga"
      element={
        <PrivateRoute>
          <SubscriptionRoute>
            <MangaAdsPage />
          </SubscriptionRoute>
        </PrivateRoute>
      }
    />

    {/* LP制作ルート */}
    <Route
      path="/ads/lp"
      element={
        <PrivateRoute>
          <FeatureRoute feature="enable_lp_creation">
            <LpCreationPage />
          </FeatureRoute>
        </PrivateRoute>
      }
    />

    {/* 音楽生成ルート */}
    <Route
      path="/ads/music"
      element={
        <PrivateRoute>
          <FeatureRoute feature="enable_music_creation">
            <MusicCreationPage />
          </FeatureRoute>
        </PrivateRoute>
      }
    />

    {/* 歌広告ルート（プロジェクト一覧・エディタ）。動画広告・漫画広告と同じく
        カード未登録ロックのみで制御する。組織ごとの機能フラグでは出し分けない（master #825 と同じ扱い）。 */}
    <Route
      path="/ads/song"
      element={
        <PrivateRoute>
          <SubscriptionRoute>
            <SongAdListPage />
          </SubscriptionRoute>
        </PrivateRoute>
      }
    />
    <Route
      path="/ads/song/:projectId"
      element={
        <PrivateRoute>
          <SubscriptionRoute>
            <SongAdEditorPage />
          </SubscriptionRoute>
        </PrivateRoute>
      }
    />

    {/* UGC動画広告ルート（プロジェクト一覧・エディタ）。動画広告・漫画広告と同じく
        カード未登録ロックのみで制御する。組織ごとの機能フラグでは出し分けない。 */}
    <Route
      path="/ads/ugc"
      element={
        <PrivateRoute>
          <SubscriptionRoute>
            <UgcAdListPage />
          </SubscriptionRoute>
        </PrivateRoute>
      }
    />
    <Route
      path="/ads/ugc/:projectId"
      element={
        <PrivateRoute>
          <SubscriptionRoute>
            <UgcAdEditorPage />
          </SubscriptionRoute>
        </PrivateRoute>
      }
    />

    {/* 動画スタイルレシピ（スタイル動画）。UGC動画広告と同じくカード未登録ロックのみで制御する */}
    <Route
      path="/ads/recipe-video"
      element={
        <PrivateRoute>
          <SubscriptionRoute>
            <RecipeVideoListPage />
          </SubscriptionRoute>
        </PrivateRoute>
      }
    />
    <Route
      path="/ads/recipe-video/:projectId"
      element={
        <PrivateRoute>
          <SubscriptionRoute>
            <RecipeVideoEditorPage />
          </SubscriptionRoute>
        </PrivateRoute>
      }
    />
    {/* 動画レシピの完成動画を本格編集で開く。中身は /chat-v2/edit と同じ画面で、動画レシピと同じくカード未登録ロックだけで通す（enable_chat_tools は見ない） */}
    <Route
      path="/ads/recipe-video/edit/:editId"
      element={
        <PrivateRoute>
          <SubscriptionRoute>
            <React.Suspense fallback={<div>Loading...</div>}>
              <VideoEditPage />
            </React.Suspense>
          </SubscriptionRoute>
        </PrivateRoute>
      }
    />

    {/* LP改善ツール（β）。動画スタイルレシピと同じくカード未登録ロックのみで制御する */}
    <Route
      path="/lp-improve"
      element={
        <PrivateRoute>
          <SubscriptionRoute>
            <React.Suspense fallback={<div>Loading...</div>}>
              <LpImproveListPage />
            </React.Suspense>
          </SubscriptionRoute>
        </PrivateRoute>
      }
    />
    <Route
      path="/lp-improve/:projectId"
      element={
        <PrivateRoute>
          <SubscriptionRoute>
            <React.Suspense fallback={<div>Loading...</div>}>
              <LpImproveWorkspacePage />
            </React.Suspense>
          </SubscriptionRoute>
        </PrivateRoute>
      }
    />

    {/* AI資料2（Document V2）ルート */}
    <Route
      path="/document-v2"
      element={
        <PrivateRoute>
          <React.Suspense fallback={<div>Loading...</div>}>
            {React.createElement(React.lazy(() => import('./pages/document-v2/DocumentV2Page')))}
          </React.Suspense>
        </PrivateRoute>
      }
    />

    {/* AI資料3（Document V3）ルート（プロジェクト一覧・エディタ）。組織フラグ enable_ai_doc3 でガードする */}
    <Route
      path="/document-v3"
      element={
        <PrivateRoute>
          <FeatureRoute feature="enable_ai_doc3">
            <React.Suspense fallback={<div>Loading...</div>}>
              {React.createElement(React.lazy(() => import('./pages/document-v3/DocumentV3ListPage')))}
            </React.Suspense>
          </FeatureRoute>
        </PrivateRoute>
      }
    />
    <Route
      path="/document-v3/:projectId"
      element={
        <PrivateRoute>
          <FeatureRoute feature="enable_ai_doc3">
            <React.Suspense fallback={<div>Loading...</div>}>
              {React.createElement(React.lazy(() => import('./pages/document-v3/DocumentV3EditorPage')))}
            </React.Suspense>
          </FeatureRoute>
        </PrivateRoute>
      }
    />
    {/* AI動画資料（Document V3 の kind=video）。AI資料3 と同じ組織フラグ enable_ai_doc3 でガードする */}
    <Route
      path="/video-document"
      element={
        <PrivateRoute>
          <FeatureRoute feature="enable_ai_doc3">
            <React.Suspense fallback={<div>Loading...</div>}>
              {React.createElement(React.lazy(() => import('./pages/video-document/VideoDocumentListPage')))}
            </React.Suspense>
          </FeatureRoute>
        </PrivateRoute>
      }
    />
    <Route
      path="/video-document/:projectId"
      element={
        <PrivateRoute>
          <FeatureRoute feature="enable_ai_doc3">
            <React.Suspense fallback={<div>Loading...</div>}>
              {React.createElement(React.lazy(() => import('./pages/video-document/VideoDocumentEditorPage')))}
            </React.Suspense>
          </FeatureRoute>
        </PrivateRoute>
      }
    />
    <Route
      path="/standalone-knowledge"
      element={
        <KnowledgeDashboard />
      }
    />
    {/* 広告ダッシュボードルート */}
    <Route
      path="/ad-dashboard"
      element={
        <PrivateRoute>
          <FeatureRoute feature="enable_ad_operations">
            <TaapDashboardRedirect />
          </FeatureRoute>
        </PrivateRoute>
      }
    />

    {/* TAAP: 同期済み実績で動く広告運用。既存の /ad-dashboard 系は
        媒体 API 直叩きのまま残し、段階的に移行する */}
    <Route
      path="/taap"
      element={
        <PrivateRoute>
          <FeatureRoute feature="enable_ad_operations">
            <TaapDashboardPage />
          </FeatureRoute>
        </PrivateRoute>
      }
    />
    <Route
      path="/taap/proposals"
      element={
        <PrivateRoute>
          <FeatureRoute feature="enable_ad_operations">
            <TaapProposalsPage />
          </FeatureRoute>
        </PrivateRoute>
      }
    />
    <Route
      path="/taap/proposals/:proposalId"
      element={
        <PrivateRoute>
          <FeatureRoute feature="enable_ad_operations">
            <TaapProposalDetailPage />
          </FeatureRoute>
        </PrivateRoute>
      }
    />

    {/* Meta 連携設定 + ライブ Meta ダッシュボード（Meta 審査用）
        直 URL で確実に到達できるよう FeatureRoute では包まない */}
    <Route
      path="/settings/media-connect"
      element={
        <PrivateRoute>
          <MediaConnectPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/ad-dashboard/meta"
      element={
        <PrivateRoute>
          <MetaDashboardPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/ad-dashboard/google"
      element={
        <PrivateRoute>
          <GoogleAdsDashboardPage />
        </PrivateRoute>
      }
    />

    {/* ニュースルート */}
    <Route
      path="/news"
      element={
        <PrivateRoute>
          <FeatureRoute feature="enable_news">
            <NewsListPage />
          </FeatureRoute>
        </PrivateRoute>
      }
    />
    <Route
      path="/news/:id"
      element={
        <PrivateRoute>
          <FeatureRoute feature="enable_news">
            <NewsDetailPage />
          </FeatureRoute>
        </PrivateRoute>
      }
    />
  </>
);

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const { token, isAuthenticated, user } = useSelector((state: RootState) => state.auth);

  // Channel Talk chat widget
  useChannelTalk();

  // アプリ起動時にトークンが存在する場合はユーザープロフィールをロード
  const [fetchAttempts, setFetchAttempts] = useState(0);
  const MAX_FETCH_ATTEMPTS = 3; // 最大リトライ回数
  const [initialCheckDone, setInitialCheckDone] = useState(false);

  // アプリケーション起動時に一度だけ実行される認証情報チェック
  useEffect(() => {
    // アプリケーション初期化時にグローバル変数を設定
    window._authInitializing = true;

    const checkAuthOnLoad = async () => {
      try {
      // トークンマネージャーからトークン情報を収集
      const storedToken = tokenManager.getToken();
      const refreshToken = tokenManager.getRefreshToken();
      const userData = tokenManager.getUserData();

      logger.log('App: Initial auth check on load', {
        hasStoredToken: !!storedToken,
        hasRefreshToken: !!refreshToken,
        hasUserData: !!userData,
        tokenInRedux: !!token,
        location: window.location.pathname
      });

        // ケース1: リフレッシュトークンがあり、アクセストークンがない場合のみリフレッシュを試みる
        if (refreshToken && !storedToken) {
          logger.log('App: Found refresh token but no access token - attempting refresh');
          try {
            const authService = (await import('./services/api/authService')).default;
            const refreshResult = await authService.refreshToken();

            if (refreshResult && refreshResult.token) {
              logger.log('App: Successfully refreshed token on load', {
                token: refreshResult.token.substring(0, 10) + '...',
                hasUserInfo: !!refreshResult.user
              });

              // Redux状態にユーザー情報を反映 + トークンマネージャーに保存
              if (refreshResult.user) {
                // ユーザー情報の設定 - authSlice内のsetUserアクションが存在しない場合はimport忘れかも
                logger.log('User info received:', refreshResult.user);
                tokenManager.saveUserData(refreshResult.user);
                dispatch(setUser(refreshResult.user)); // ★ Reduxストアも更新
              }

              // トークンマネージャーにトークンを保存
              logger.log('New token received:', refreshResult.token.substring(0, 10) + '...');
              tokenManager.saveToken(refreshResult.token);
              dispatch(setToken(refreshResult.token)); // ★ Reduxストアも更新
            } else {
              console.warn('App: Refresh token available but refresh failed');
              // トークンリフレッシュ失敗時は認証情報を削除しない
              console.warn('App: Token refresh failed but keeping existing auth data');
            }
          } catch (error: any) {
            console.error('App: Failed to refresh token on initial load', error);
            // 401/422エラーの場合は無効なリフレッシュトークンなのでクリア
            if (error.response && (error.response.status === 401 || error.response.status === 422)) {
              console.warn('App: Invalid refresh token detected, clearing auth data');
              tokenManager.clearAllAuthData();
            }
          }
        }
        // ケース2: アクセストークンとリフレッシュトークンの両方がある場合
        else if (storedToken && refreshToken) {
          logger.log('App: Both tokens exist - using existing authentication');
          // 既存のトークンをReduxストアに設定
          dispatch(setToken(storedToken));
          if (userData) {
            dispatch(setUser(userData));
          }
        }
        // ケース3: トークンがあるがリフレッシュトークンがない場合
        else if (storedToken && !refreshToken) {
          console.warn('App: Access token exists but no refresh token - may be unstable');
          // それでも既存のトークンを使用
          dispatch(setToken(storedToken));
          if (userData) {
            dispatch(setUser(userData));
          }
        }
        // ケース4: どちらもない場合はログアウト状態
        else {
          logger.log('App: No tokens found - user is not authenticated');
        }

        // 保存済みユーザー情報は即時表示用として使い、権限などの最新状態は必ずAPIで同期する
        if (tokenManager.getToken()) {
          try {
            logger.log('App: Fetching latest user profile on initial load');
            await dispatch(getUserProfile()).unwrap();
          } catch (profileError) {
            console.warn('App: Failed to fetch latest user profile on initial load', profileError);
          }
        }
      } catch (error) {
        console.error('App: Error during initial auth check', error);
      } finally {
        logger.log('App: Initial auth check on load done');
        // 初期化完了を設定
        window._authInitializing = false;
        setInitialCheckDone(true);
      }
    };

    // 少し遅延させて初期化（他のコンポーネントの初期化前に実行するため）
    setTimeout(() => {
      logger.log('App: Starting initial auth check on load');
      checkAuthOnLoad();
    }, 0);

    // クリーンアップ関数
    return () => {
      window._authInitializing = false;
    };
  }, [dispatch]);  // dispatch依存を追加

  // 通常の認証状態管理（初期チェック完了後に実行）
  useEffect(() => {
    if (!initialCheckDone) return;  // 初期チェックが完了するまで待機

    logger.log('App: Checking authentication state');
    logger.log('Token exists:', !!token);
    logger.log('Is authenticated:', isAuthenticated);
    logger.log('User exists:', !!user);
    logger.log('Fetch attempts:', fetchAttempts);

    // トークンがあってユーザー情報がない場合、かつ最大リトライ回数未満の場合のみ取得を試みる
    if (token && !user && fetchAttempts < MAX_FETCH_ATTEMPTS) {
      logger.log(`App: Token exists but user is null, fetching user profile (attempt ${fetchAttempts + 1}/${MAX_FETCH_ATTEMPTS})`);
      setFetchAttempts(prev => prev + 1);

      try {
        dispatch(getUserProfile());
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
      }
    } else if (token && !user && fetchAttempts >= MAX_FETCH_ATTEMPTS) {
      // 最大リトライ回数に達した場合
      console.warn('Max fetch attempts reached');

      // アクセストークンが有効であれば認証状態を維持
      const token = tokenManager.getToken();
      const refreshToken = tokenManager.getRefreshToken();

      logger.log('Max fetch attempts reached - checking token status', {
        hasToken: !!token,
        hasRefreshToken: !!refreshToken,
        tokenIsValid: tokenManager.hasValidToken()
      });

      if (tokenManager.hasValidToken()) {
        logger.log('Access token is still valid - maintaining login state');
        // アクセストークンが有効な場合は認証状態を維持
        // ユーザー情報取得の再試行はページリロード時に行われる
      } else {
        // アクセストークンが無効またはない場合のみログアウト
        console.warn('No valid token found - logging out');
        tokenManager.clearTokens(false);
      }
    }
  }, [dispatch, token, isAuthenticated, user, fetchAttempts, initialCheckDone]);

  // 初期チェック中はローディング表示
  if (!initialCheckDone) {
    return <div>認証情報チェック中...</div>;
  }

  return (
    <ToastProvider>
    <ConfirmProvider>
    <HeaderActionsProvider>
    <Router>
      <MetaPixelRouteTracker />
      <BehaviorTrackerRoot />
      <TutorialHost />
      <Routes>
        {/* 認証ルート */}
        <Route path="/login" element={<LoginPage />} />
        {/* 旧 /register は新 Magic Link / Google フローへ統合（SignupPage へリダイレクト） */}
        <Route path="/register" element={<Navigate to="/signup" replace />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />

        {/* Magic Link 認証フロー - 認証不要 */}
        <Route path="/auth/magic-link-sent" element={<MagicLinkSentPage />} />
        <Route path="/auth/complete" element={<CompleteSignInPage />} />

        {/* 招待承認ルート - 認証不要 */}
        <Route path="/invite/accept" element={<InviteAcceptPage />} />

        {/* 既存 /onboarding は LP→URL分析→バナー生成→プラン選択の別フロー。
            Stripe Checkout の success_url/cancel_url が依存しているため変更禁止。 */}
        <Route path="/onboarding" element={<OnboardingPage />} />

        {/* LP関連パブリックルート - 認証不要 */}
        <Route path="/lp" element={<LandingPage />} />
        <Route path="/signup" element={<SignupPage />} />
        {/* Magic Link / Google 認証後の会社情報入力ステップ */}
        <Route path="/signup/setup" element={<CompanyOnboardingPage />} />
        {/* 会社情報入力後のプロダクト体験オンボーディング（認証済み・組織所属済み） */}
        <Route path="/signup/onboarding" element={<SignupOnboardingPage />} />

        {/* 診断・レポート関連パブリックルート - 認証不要 */}
        <Route path="/diagnose" element={<DiagnosePage />} />
        <Route path="/analyzing" element={<AnalyzingPage />} />
        <Route path="/complete" element={<CompletePage />} />
        <Route path="/report/:slug" element={<ReportPage />} />

        {/* 漫画広告の公開ページ - 認証不要（共有リンク） */}
        <Route path="/m/:token" element={<MangaPublicPage />} />

        {/* プラン選択ページ - 認証不要（Checkout成功後のリダイレクト先としても使用） */}
        <Route path="/plans" element={<PlansPage />} />

        {/* 初回ログインページ - 認証不要 */}
        <Route path="/first-login" element={<FirstLoginPage />} />

        {/* テストページ - 認証不要 */}
        <Route
          path="/test"
          element={
            <React.Suspense fallback={<div>Loading...</div>}>
              <TestPage />
            </React.Suspense>
          }
        />

        {/* Tasky デザインシステムカタログ - 認証不要 */}
        <Route
          path="/design-system"
          element={
            <React.Suspense fallback={<div>Loading...</div>}>
              <DesignSystemTestPage />
            </React.Suspense>
          }
        />

        {/* ドキュメント作成テストページ - 認証不要 */}
        <Route path="/document-creation-test" element={<DocumentCreationTest />} />

        {/* Firebase認証テストページ - 認証不要 */}
        <Route
          path="/test/firebase-auth"
          element={
            <React.Suspense fallback={<div>Loading...</div>}>
              {React.createElement(React.lazy(() => import('./pages/test/FirebaseAuthTest')))}
            </React.Suspense>
          }
        />

        {/* プライベートルート */}
        <Route
          path="/"
          element={<NavigateWithQuery to="/home" />}
        />
        <Route
          path={V2_BASE}
          element={
            <PrivateRoute>
              <React.Suspense fallback={null}>
                <V2Layout />
              </React.Suspense>
            </PrivateRoute>
          }
        >
          <Route index element={<V2HomePage />} />
          <Route path="banner" element={<V2BannerPage />} />
          {/* 旧 /ads/video と同じくカード未登録ロックを掛ける */}
          <Route
            path="video"
            element={
              <V2RouteGuard guard={{ kind: 'card' }}>
                <V2VideoPage />
              </V2RouteGuard>
            }
          />
          <Route path="products" element={<V2ProductsPage />} />
          <Route path="products/:id" element={<V2ProductDetailPage />} />
          {V2_UNIT_ROUTES.map(({ path, Component, guard }) => (
            <Route
              key={path}
              path={path}
              element={
                <V2RouteGuard guard={guard}>
                  <Component />
                </V2RouteGuard>
              }
            />
          ))}
          {/* 新UIで作り直していない機能は、旧画面をこの枠の中に置く */}
          <Route path="*" element={<V2LegacyFrame>{appRoutes}</V2LegacyFrame>} />
        </Route>
        {appRoutes}

        {/* 管理者ルート */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <Navigate to="/admin/users" replace />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/organizations/:id"
          element={
            <AdminRoute>
              <OrganizationDetailPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users/:id"
          element={
            <AdminRoute>
              <AdminUserDetailPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/:tab"
          element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          }
        />

        {/* デフォルトルート */}
        <Route path="*" element={<NavigateWithQuery to="/" />} />
      </Routes>
    </Router>
    </HeaderActionsProvider>
    </ConfirmProvider>
    </ToastProvider>
  );
};

export default App;
